import clsx from 'clsx';
import * as React from 'react';

import {
    type IconDefinition,
    KeyboardShortcut,
    ListItem,
    ListItemBody,
    ListItemDescription,
    ListItemDetail,
    ListItemLeading,
    renderIcon,
    TextMatchHighlight,
} from '@gitbook/spine-react';

export interface SearchResultInfo {
    icon?: IconDefinition;
    title: React.ReactNode;
    action: string;
    subtitle?: string;
    body?: string;
    disabled?: boolean;
    standout?: boolean;
    /** Query string used to highlight matching text */
    query?: string;
}

export function SearchResultItem({
    ref,
    ...props
}: (SearchResultInfo & {
    active: boolean;
    onSelect: () => void;
    onHover: () => void;
    onFocus: () => void;
}) & { ref?: React.Ref<HTMLLIElement> }): React.ReactElement {
    const {
        title,
        subtitle,
        body,
        icon: IconCo,
        active,
        disabled = false,
        action,
        standout = false,
        query,
        onHover,
        onSelect,
        onFocus,
    } = props;

    return (
        <ListItem
            ref={ref}
            active={active}
            selected={active}
            onPress={disabled ? undefined : onSelect}
            onMouseEnter={onHover}
            onFocus={onFocus}
            size="medium"
            className={clsx(
                'relative',
                disabled ? 'opacity-disabled' : undefined,
                // Hovering causes the list item's bg to change to a color that is also used for accents within the item (e.g. the quote line). To avoid it becoming indistinguishable due to the bg having the same color, we adapt the colors along with it.
                '[--highlight-color:var(--neutral-3)] focus-within:[--highlight-color:var(--neutral-5)] hover:[--highlight-color:var(--neutral-5)] aria-selected:[--highlight-color:var(--neutral-7)]',
                // Same trick but to adapt the list item's bg color, used for the gradient fade at the right side of the item under the keyboard shortcut.
                '[--list-bg-color:var(--neutral-3)] focus-within:[--list-bg-color:var(--neutral-4)] hover:[--list-bg-color:var(--neutral-3)] aria-selected:[--list-bg-color:var(--neutral-4)]'
            )}
        >
            {IconCo ? (
                <ListItemLeading>
                    {renderIcon(IconCo, { size: 18, className: 'text-neutral-low' })}
                </ListItemLeading>
            ) : null}
            <ListItemBody>
                {title ? (
                    <ListItemDescription>
                        {typeof title === 'string' && query && !standout ? (
                            <TextMatchHighlight text={title} query={query} />
                        ) : (
                            title
                        )}
                    </ListItemDescription>
                ) : null}
                {subtitle ? (
                    <ListItemDetail>
                        {query && !standout ? (
                            <TextMatchHighlight text={subtitle} query={query} />
                        ) : (
                            subtitle
                        )}
                    </ListItemDetail>
                ) : null}
                {body ? (
                    <ListItemDetail className="ml-1 line-clamp-3 border-l-2 border-l-[var(--highlight-color)] pl-3 text-neutral-low">
                        {query && !standout ? (
                            <TextMatchHighlight text={body} query={query} />
                        ) : (
                            body
                        )}
                    </ListItemDetail>
                ) : null}
            </ListItemBody>
            <SearchResultKeyboardShortcut active={active} action={action} />
        </ListItem>
    );
}

function SearchResultKeyboardShortcut(props: {
    active: boolean;
    action: string;
}): React.ReactElement {
    const { active, action } = props;
    return (
        <div
            className={clsx(
                // A keyboard hint, and it hides the end of the title: no use on a touch screen.
                'absolute top-1/2 right-0 h-full -translate-y-1/2 rounded-r-xl bg-gradient-to-l from-[var(--list-bg-color)] from-80% to-transparent pr-3 pl-8 pointer-coarse:hidden',
                active
                    ? 'flex'
                    : 'hidden group-hover/listitem:flex group-focus-visible/listitem:flex'
            )}
        >
            <div className="flex items-center gap-1 text-ui-small text-neutral-low">
                {action}
                <KeyboardShortcut shortcut="Enter" />
            </div>
        </div>
    );
}
